import { createContext, createElement, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import { api } from "./api.js";

/**
 * The event stream, and the one way a page keeps itself current.
 *
 * A change is pushed, not polled for. The server keeps `GET /api/events` open
 * to every signed-in tab and says what changed — a deployment moved, a session
 * started, the monitor passed — and `useLive` refetches what a page shows the
 * moment it hears. Nothing on a page asks on a timer any more; the timer that
 * is left is a slow safety net for an event that was missed, and it is never
 * what makes the screen move.
 *
 * The stream's first event names the release the server runs from. A tab that
 * has seen a different one is running an old build: it reloads itself when
 * that is safe, and asks when it is not — a form half filled in is not the
 * moment.
 */

const FALLBACK_MS = 60_000;
/** Reconnect and visibility refetches are collapsed to one within this. */
const SETTLE_MS = 250;
/** And skipped altogether when the page fetched this recently anyway. */
const RECENT_MS = 1_000;

const LiveContext = createContext({ subscribe: () => () => {}, connected: false, update: null, reload: () => {} });

/** Whether reloading now would lose something somebody typed. */
export function safeToReload() {
    const active = document.activeElement;
    const typing = active && (/^(input|textarea|select)$/i.test(active.tagName) || active.isContentEditable);
    return !typing && !document.querySelector("[data-dirty]");
}

export function LiveProvider({ children }) {
    const listeners = useRef(new Set());
    const release = useRef(null);
    const [connected, setConnected] = useState(false);
    const [update, setUpdate] = useState(null);
    const location = useLocation();

    const fanOut = useCallback((event) => {
        for (const listener of listeners.current) listener(event);
    }, []);

    useEffect(() => {
        const source = new EventSource(api.events.url());
        source.onopen = () => {
            setConnected(true);
            // Whatever happened while the stream was down is refetched now.
            fanOut({ kind: "*", at: new Date().toISOString(), reason: "connected" });
        };
        source.onerror = () => setConnected(false);
        source.addEventListener("hello", (message) => {
            const { release: current } = JSON.parse(message.data);
            if (release.current === null) release.current = current;
            else if (current !== release.current) setUpdate(current);
        });
        for (const kind of ["deployments", "projects", "sessions", "workspaces", "status", "system", "notifications", "audit", "env"]) {
            source.addEventListener(kind, (message) => fanOut(JSON.parse(message.data)));
        }
        // Closed by hand when the page goes: Chrome leaves a navigated-away
        // document's stream open until it gets round to destroying the
        // document, and six of those fill the connection pool for the host —
        // after which nothing on the next page loads at all.
        const close = () => source.close();
        window.addEventListener("pagehide", close);
        return () => {
            window.removeEventListener("pagehide", close);
            source.close();
        };
    }, [fanOut]);

    // A new build reloads the tab the moment that costs nothing: at once when
    // nobody is typing, otherwise on the next navigation — leaving the form is
    // the end of the form.
    useEffect(() => {
        if (update && safeToReload()) window.location.reload();
    }, [update, location.pathname]);

    const subscribe = useCallback((listener) => {
        listeners.current.add(listener);
        return () => listeners.current.delete(listener);
    }, []);

    const reload = useCallback(() => window.location.reload(), []);

    return createElement(LiveContext.Provider, { value: { subscribe, connected, update, reload } }, children);
}

/** The stream's state, for the shell: is it connected, is a newer build waiting. */
export function useLiveState() {
    return useContext(LiveContext);
}

/**
 * Keep `load` current.
 *
 * Called now; on every event whose kind is listed (the event is passed, so a
 * project page can ignore another project's); whenever the stream connects or
 * reconnects; whenever the tab is shown again — iOS drops the connection in
 * the background; and every `every` ms as the net under all of that. `kinds`
 * may include "*" to hear everything. `key` is what the page is about — a
 * slug from the URL — so a page that stays mounted while it changes loads
 * again.
 */
export function useLive(kinds, load, { every = FALLBACK_MS, key = null } = {}) {
    const { subscribe } = useContext(LiveContext);
    const wanted = useRef(kinds);
    wanted.current = kinds;
    const latest = useRef(load);
    latest.current = load;

    useEffect(() => {
        let settle = null;
        let ran = 0;
        const run = (event) => {
            ran = Date.now();
            latest.current(event);
        };
        // A reconnect and a visibility change often arrive together, and the
        // stream connects moments after a page mounts; one fetch will do.
        const soon = (event) => {
            clearTimeout(settle);
            settle = setTimeout(() => Date.now() - ran > RECENT_MS && run(event), SETTLE_MS);
        };

        run({ kind: "*", reason: "mounted" });

        const stop = subscribe((event) => {
            if (event.kind === "*") return soon(event);
            if (wanted.current.includes("*") || wanted.current.includes(event.kind)) run(event);
        });
        const onVisible = () => document.visibilityState === "visible" && soon({ kind: "*", reason: "visible" });
        document.addEventListener("visibilitychange", onVisible);
        const timer = every ? setInterval(() => run({ kind: "*", reason: "fallback" }), every) : null;

        return () => {
            stop();
            clearTimeout(settle);
            if (timer) clearInterval(timer);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [subscribe, every, key]);
}
